import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { CoffeeViewNavState, SearchResultsNavState } from '@/analytics/events';
import { newSearchId, trackCoffeeSearched, trackSearchResultClicked } from '@/analytics/events';
import { MIN_QUERY_LENGTH } from '@/api/queries/search';
import { useDebounce } from '@/hooks/useDebounce';
import { useSearchCoffees } from '@/hooks/useSearchCoffees';
import type { SearchCoffeeResult } from '@/types/search';

import { SearchDropdown } from './SearchDropdown';

const DEBOUNCE_MS = 200;
const DROPDOWN_LIMIT = 5;

interface SearchBarProps {
  autoFocusOnMount?: boolean;
  onNavigate?: () => void;
  className?: string;
  inputClassName?: string;
  // `floating` (default) is the desktop popover; `inline` is the mobile
  // full-screen surface, which owns its own red bar and renders results as a
  // flowing list below the input rather than a floating panel.
  resultsLayout?: 'floating' | 'inline';
  // Rendered next to the input in `inline` mode (e.g. the overlay's Cancel).
  trailing?: React.ReactNode;
}

export function SearchBar({
  autoFocusOnMount = false,
  onNavigate,
  className = '',
  inputClassName = '',
  resultsLayout = 'floating',
  trailing,
}: SearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const trimmed = debouncedQuery.trim();

  // Dedupe `coffee_searched`: fire once per settled query, not per render.
  const lastSearchedRef = useRef<string | null>(null);
  // One `search_id` per settled, valid query (#448). A ref keyed on the
  // debounced query gives a *stable* identity: it is regenerated only when
  // `trimmed` actually transitions (including re-typing the same text after a
  // clear, since `trimmed` transits through '' in between), and — unlike
  // `useMemo`, which React may discard and recompute — never silently re-mints
  // for an unchanged query. That stability is load-bearing: `coffee_searched`
  // dedupes on the query, so a re-minted id would emit clicks under an id that
  // has no matching `coffee_searched`. The emit and every click handler read
  // this same value. (`SearchResultsPage` uses the same ref-keyed approach.)
  const searchIdRef = useRef<{ query: string; id: string } | null>(null);
  if (trimmed.length < MIN_QUERY_LENGTH) {
    searchIdRef.current = null;
    // Clear the dedupe so re-typing the same term after a clear counts as a
    // fresh search (matters for abandonment/retry analysis).
    lastSearchedRef.current = null;
  } else if (searchIdRef.current?.query !== trimmed) {
    searchIdRef.current = { query: trimmed, id: newSearchId() };
  }
  const searchId = searchIdRef.current?.id ?? null;

  const { results, isLoading } = useSearchCoffees(debouncedQuery, DROPDOWN_LIMIT, 0, {
    // Emit `coffee_searched` from the settled result, not an `isLoading`-gated
    // effect: `useSearchCoffees` starts `isLoading` false and only flips it true
    // in a post-commit fetch effect, so an effect would fire in the render
    // before loading begins — while `results` is still empty — and record
    // `result_count: 0` even for a query that has matches. `onResult` fires with
    // the real result set for the current query instead.
    // A failed (or aborted) fetch intentionally emits nothing: an errored search
    // is not a zero-result search, and emitting one with a stale/empty count
    // would pollute zero-result analysis. This matches `SearchResultsPage`, whose
    // `onFirstPage` likewise only fires on success.
    onResult: (resultQuery, resultList) => {
      if (lastSearchedRef.current === resultQuery) return;
      lastSearchedRef.current = resultQuery;
      trackCoffeeSearched({
        query: resultQuery,
        result_count: resultList.length,
        surface: 'dropdown',
        search_id:
          searchIdRef.current?.query === resultQuery
            ? searchIdRef.current.id
            : newSearchId(),
      });
    },
  });

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [debouncedQuery]);

  useEffect(() => {
    if (autoFocusOnMount) {
      inputRef.current?.focus();
    }
  }, [autoFocusOnMount]);

  useEffect(() => {
    // The inline (mobile) surface fills the screen, so there is no "outside" to
    // dismiss to — keeping the listener would hide results on any tap in the
    // empty area below them.
    if (resultsLayout !== 'floating') {
      return;
    }
    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resultsLayout]);

  const showDropdown = isOpen && trimmed.length >= MIN_QUERY_LENGTH;

  function goToCoffee(result: SearchCoffeeResult) {
    // `query`/`position`/`search_id` describe the dropdown the user actually
    // clicked — i.e. the settled debounced `results`. If they kept typing and
    // clicked before the next debounce cycle, these intentionally reflect what
    // was on screen, not the newer in-flight query.
    const sid = searchId ?? newSearchId();
    const position = results.findIndex((r) => r.id === result.id);
    trackSearchResultClicked({
      query: trimmed,
      surface: 'dropdown',
      target: 'coffee',
      position: position >= 0 ? position : undefined,
      search_id: sid,
    });
    setIsOpen(false);
    onNavigate?.();
    void navigate(`/coffee/${result.id}`, {
      state: { source: 'search_dropdown', search_id: sid } satisfies CoffeeViewNavState,
    });
  }

  function goToResultsPage() {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      return;
    }
    // Carry the dropdown attempt's id onto the results page so "view all"
    // continues the same logical search (the results page reuses it when its
    // query matches). Reuse only when `q` is the settled query the id was minted
    // for; on Enter-before-debounce `q` is the raw input and differs from
    // `trimmed`, so mint a fresh id rather than pin one attempt across two
    // distinct queries.
    const sid = searchId !== null && q === trimmed ? searchId : newSearchId();
    trackSearchResultClicked({
      query: q,
      surface: 'dropdown',
      target: 'view_all',
      search_id: sid,
    });
    setIsOpen(false);
    onNavigate?.();
    void navigate(`/search?q=${encodeURIComponent(q)}`, {
      state: { search_id: sid, query: q } satisfies SearchResultsNavState,
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (results.length > 0) {
        setHighlightedIndex((prev) => (prev + 1) % results.length);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length > 0) {
        setHighlightedIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && results[highlightedIndex]) {
        goToCoffee(results[highlightedIndex]);
      } else {
        goToResultsPage();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }

  const inputBox = (
    <div className="flex items-center overflow-hidden rounded-lg bg-wolisho-cream">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="search-dropdown"
        aria-autocomplete="list"
        placeholder="Search coffees"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className={`flex-1 px-4 py-2 text-gray-700 placeholder:text-gray-400 focus:outline-hidden ${inputClassName}`}
      />
    </div>
  );

  if (resultsLayout === 'inline') {
    return (
      <div ref={containerRef} className={`flex min-h-0 flex-col ${className}`}>
        <div className="flex items-center gap-2 bg-wolisho-red px-4 py-3">
          <div className="flex-1">{inputBox}</div>
          {trailing}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-wolisho-cream" id="search-dropdown">
          {showDropdown && (
            <SearchDropdown
              query={trimmed}
              results={results}
              isLoading={isLoading}
              highlightedIndex={highlightedIndex}
              onSelect={goToCoffee}
              onViewAll={goToResultsPage}
              searchId={searchId ?? newSearchId()}
              layout="inline"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {inputBox}

      {showDropdown && (
        <div id="search-dropdown">
          <SearchDropdown
            query={trimmed}
            results={results}
            isLoading={isLoading}
            highlightedIndex={highlightedIndex}
            onSelect={goToCoffee}
            onViewAll={goToResultsPage}
            searchId={searchId ?? newSearchId()}
          />
        </div>
      )}
    </div>
  );
}
